import { toDate } from 'date-fns'
import { da } from 'date-fns/esm/locale'
import Excel, {
    Column,
    Fill,
    Borders,
    // Cell
} from 'exceljs'
import { ExcelPeriod, IReportDataDate, IReportDataInput } from './types'
import countryData from '../../lib/countries.json'
import { formatText, layoutDictKeyFormat } from '../../lib/dateFunctions'

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
const reportDateFormat = layoutDictKeyFormat

const alphabet = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
]

const setAlignmentHorizontal = (cell, periodDate = null) => {
    cell.alignment = {
        horizontal: 'center',
    }
}

const setAlignmentWrapText = (cell, periodDate = null) => {
    cell.alignment = { wrapText: true }
}

const dataCellFill: Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'EEECE1' },
}

const dataCellBorder: Borders = {
    top: { style: 'thin', color: { argb: 'AAAAAA' } },
    left: { style: 'thin', color: { argb: 'AAAAAA' } },
    bottom: { style: 'thin', color: { argb: 'AAAAAA' } },
    right: { style: 'thin', color: { argb: 'AAAAAA' } },
    diagonal: {},
}

const metaFont = {
    bold: true,
}

function initDocument(reportData: IReportDataInput) {
    const workbook = new Excel.Workbook()
    workbook.creator = reportData.userName
    workbook.lastModifiedBy = reportData.userName
    workbook.created = new Date()
    workbook.modified = new Date()
    workbook.lastPrinted = new Date()
    workbook.views = [
        {
            x: 0,
            y: 0,
            width: 10000,
            height: 20000,
            firstSheet: 0,
            activeTab: 1,
            visibility: 'visible',
        },
    ]
    const period: ExcelPeriod = reportData.data
    const sheet = workbook.addWorksheet(
        `period-${formatText(toDate(new Date(period.start)), reportDateFormat)}-${
            period.end ? formatText(toDate(new Date(period.end)), reportDateFormat) : 'ukendt'
        }`,
        {
            views: [{ state: 'frozen', xSplit: 0, ySplit: 7 }],
        }
    )
    return { workbook, sheet }
}

function addOptionalColumns(
    reportData: IReportDataInput,
    hasRoster: boolean,
    cols: Column[],
    convCol,
    cellsConfig
) {
    if (reportData.includeDoc) {
        cols.push({ ...convCol, ...cellsConfig.apx })
    }
    if (reportData.includeNotes) {
        cols.push({ ...convCol, ...cellsConfig.notes })
    }
    if (reportData.includeLocations) {
        cols.push({
            ...convCol,
            ...cellsConfig.locations,
        })
    }
    if (hasRoster) {
        cols.push({ ...convCol, ...cellsConfig.dutyNotes })
    }
}

function createColumns(
    reportData: IReportDataInput,
    hasRoster: boolean,
    sheet: Excel.Worksheet,
    cellsConfig
) {
    const convCol = {
        hidden: false,
        outlineLevel: 0,
        style: {},
        values: [0],
        letter: 'A',
        number: 1,
        worksheet: sheet,
        isCustomWidth: true,
        isDefault: true,
        headerCount: 1,
        headers: [],
        equivalentTo: (other: Column): boolean => false,
        collapsed: false,
        eachCell: null,
        // eachCell: dummy2,
        defn: '',
    }
    let cols: Column[] = [
        { ...convCol, ...cellsConfig.date },
        { ...convCol, ...cellsConfig.dkDay },
        { ...convCol, ...cellsConfig.dayCount },
        { ...convCol, ...cellsConfig.exceeding },
    ]
    addOptionalColumns(reportData, hasRoster, cols, convCol, cellsConfig)
    return cols
}

function getNotesValue(periodDate: IReportDataDate) {
    return periodDate.user_note_include_report && periodDate.note_content
        ? periodDate.note_content
        : null
}

function getRosterValue(periodDate: IReportDataDate) {
    return periodDate.roster_categories ? 'x' : null
}

function getLocationsValue(periodDate: IReportDataDate) {
    if (!periodDate.user_locations || periodDate.user_locations.length < 1) return
    return periodDate.user_locations
        .map((l) => {
            const thisCountryData = countryData[l.country_code.toString().toUpperCase()]
            return `${thisCountryData?.emoji ?? l.country_code.toString().toUpperCase()} ${
                thisCountryData?.name ?? ''
            } ${l.location_name}`
        })
        .join(',\n')
}

export async function createExcelFile(
    reportData: IReportDataInput,
    callBack: (meta: any) => void,
    returnExcel = false // return excel file or return zip with roster
) {
    const period: ExcelPeriod = reportData.data

    function getRosterMap(reportData: IReportDataInput) {
        const hasRoster: boolean =
            period.has_roster &&
            period.roster_periods.length > 0 &&
            !reportData.disableRosterOverride
        var rosterMap = {}
        if (hasRoster) {
            for (var i = 0; i < period.roster_periods.length; i++) {
                rosterMap[period.roster_periods[i].id] = period.roster_periods[i].pdf_file.filename
            }
        }
        return { rosterMap, hasRoster }
    }

    const rosterConf = getRosterMap(reportData)
    const hasRoster = rosterConf.hasRoster
    const rosterMap = rosterConf.rosterMap
    const document: any = initDocument(reportData)
    const workbook = document.workbook
    const sheet: Excel.Worksheet = document.sheet

    function getDocValue(periodDate: IReportDataDate) {
        var apxRef: string[] = []

        // star air roster
        if (periodDate.roster_id && hasRoster) {
            if (periodDate.roster_id in rosterMap) {
                apxRef.push(rosterMap[periodDate.roster_id])
            }
        }

        if (reportData.includeDoc && periodDate.appendixref) {
            for (var idx = 0; idx < periodDate.appendixref.length; idx++) {
                if (!periodDate.appendixref[idx].include_report) {
                    continue
                }
                apxRef.push(periodDate.appendixref[idx].filename)
            }
        }
        const apxStr = apxRef.join(',\n')
        return apxStr
    }

    const cellsConfig = {
        date: {
            key: 'date',
            header: 'Dato',
            width: 14,
            getVal: (periodDate: IReportDataDate) => {
                return formatText(toDate(new Date(periodDate.date)), reportDateFormat, {
                    locale: da,
                })
            },
            alterCell: (cell) => {
                setAlignmentHorizontal(cell)
                cell.border = dataCellBorder
            },
        },
        dkDay: {
            key: 'dkDay',
            header: 'DK-dag\n(x for ja)',
            width: 10,
            getVal: (periodDate: IReportDataDate) => (periodDate.wasindk ? 'x' : null),
            alterCell: (cell) => {
                setAlignmentHorizontal(cell)
                cell.border = dataCellBorder
            },
        },
        dayCount: {
            key: 'dayCount',
            header: 'Sum af DK-dage\n(6 måneder bagud)',
            width: 13,
            getVal: (periodDate: IReportDataDate) => periodDate.daycount,
            alterCell: (cell) => {
                setAlignmentHorizontal(cell)
                cell.border = dataCellBorder
            },
        },
        exceeding: {
            key: 'exceeding',
            header: '42 dages reglen\n(x for overskredet)',
            width: 10,
            getVal: (periodDate: IReportDataDate) => (periodDate.daycount > 42 ? 'x' : null),
            alterCell: (cell, periodDate) => {
                setAlignmentHorizontal(cell)
                cell.border = dataCellBorder
                if (periodDate.daycount > 42) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFF0000' },
                    }
                }
            },
        },
        apx: {
            key: 'apx',
            header: 'Bilags referencer',
            width: 20,
            getVal: getDocValue,
            alterCell: (cell, periodDate) => {
                setAlignmentWrapText(cell)
                cell.fill = dataCellFill
                cell.border = dataCellBorder
            },
        },
        notes: {
            key: 'notes',
            header: 'Bemærkninger',
            width: 20,
            getVal: getNotesValue,
            alterCell: (cell, periodDate) => {
                setAlignmentWrapText(cell)
                cell.fill = dataCellFill
                cell.border = dataCellBorder
            },
        },
        locations: {
            key: 'locations',
            header: 'Opholdssteder',
            width: 20,
            getVal: getLocationsValue,
            alterCell: (cell, periodDate) => {
                setAlignmentWrapText(cell)
                cell.fill = dataCellFill
                cell.border = dataCellBorder
            },
        },
        dutyNotes: {
            key: 'dutyNotes',
            header: 'Arbejdsdag\n(x for ja)',
            width: 13,
            getVal: getRosterValue,
            alterCell: (cell, periodDate) => {
                setAlignmentHorizontal(cell)
                cell.fill = dataCellFill
                cell.border = dataCellBorder
            },
        },
    }

    const sheetColumns = createColumns(reportData, hasRoster, sheet, cellsConfig)
    sheet.columns = sheetColumns

    function getColumnIndex(key) {
        let idx = 1
        for (const col of sheetColumns) {
            if (col.key === key) return idx
            idx += 1
        }
        return null
    }

    function addRow(periodDate: IReportDataDate) {
        var newRow = {}
        for (const col of sheetColumns) {
            const colConfig = cellsConfig[col.key]
            newRow[col.key] = colConfig.getVal(periodDate)
        }
        const row = sheet.addRow(newRow)
        for (const col of sheetColumns) {
            const colConfig = cellsConfig[col.key]
            colConfig.alterCell(row.getCell(getColumnIndex(col.key)), periodDate)
        }
    }

    function addRows() {
        for (var rowsIdx = 0; rowsIdx < period.dates.length; rowsIdx++) {
            const periodDate = period.dates[rowsIdx]
            addRow(periodDate)
        }
    }
    addRows()

    const headerRows = [
        [
            {
                content: 'Navn:',
                col_start: 0,
                alterCell: (cell) => {
                    cell.alignment = { wrapText: true, horizontal: 'right' }
                    cell.font = metaFont
                    cell.border = dataCellBorder
                },
                alterCellKey: 'A1',
            },
            {
                content: reportData.full_name,
                col_start: 1,
                merge: sheetColumns.length > 4 ? 'B1:C1' : null,
                alterCell: (cell) => {
                    cell.font = metaFont
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'D3D3D3' },
                    }
                },
                alterCellKey: 'B1',
            },
            {
                content: 'Cpr.nr.:',
                col_start: sheetColumns.length > 4 ? 3 : 2,
                alterCell: (cell) => {
                    cell.alignment = { wrapText: true, horizontal: 'right' }
                    cell.font = metaFont
                    cell.border = dataCellBorder
                },
                alterCellKey: `${alphabet[sheetColumns.length > 4 ? 3 : 2]}1`,
            },
            {
                content: reportData.person_id,
                col_start: sheetColumns.length > 4 ? 4 : 3,
                merge:
                    sheetColumns.length > 5
                        ? `${sheetColumns.length > 4 ? alphabet[4] : alphabet[3]}1:${
                              alphabet[sheetColumns.length - 1]
                          }1`
                        : null,
                alterCell: (cell) => {
                    cell.font = metaFont
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'D3D3D3' },
                    }
                    cell.border = dataCellBorder
                },
                alterCellKey: `${sheetColumns.length > 4 ? alphabet[4] : alphabet[3]}1`,
            },
        ],
        [
            {
                content: 'BEREGNING AF UDLANDSOPHOLD I HENHOLD TIL LIGNINGSLOVEN § 33 A.',
                col_start: 0,
                merge: `A2:${alphabet[sheetColumns.length - 1]}2`,
                alterCell: (cell) => {
                    cell.alignment = { wrapText: true, horizontal: 'center' }
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'DDD9C3' },
                    }
                    cell.font = {
                        bold: true,
                        size: 20,
                    }
                    cell.border = dataCellBorder
                },
                alterCellKey: 'A2',
            },
        ],
        [
            {
                content: 'Startdato for udlandsophold:',
                col_start: 0,
                merge: `A3:${alphabet[sheetColumns.length - 2]}3`,
            },
            {
                content: formatText(toDate(new Date(period.start)), reportDateFormat),
                col_start: sheetColumns.length - 1,
                alterCell: (cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'D3D3D3' },
                    }
                    cell.border = dataCellBorder
                },
                alterCellKey: `${alphabet[sheetColumns.length - 1]}3`,
            },
        ],
        [
            {
                content: 'Slutdato for udlandsophold (hvis kendt):',
                col_start: 0,
                merge: `A4:${alphabet[sheetColumns.length - 2]}4`,
                alterCell: (cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'D3D3D3' },
                    }
                    cell.border = dataCellBorder
                },
                alterCellKey: `${alphabet[sheetColumns.length - 1]}4`,
            },
            {
                content: period.end
                    ? formatText(toDate(new Date(period.end)), reportDateFormat)
                    : 'ukendt',
                col_start: sheetColumns.length - 1,
                alterCell: (cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'D3D3D3' },
                    }
                    cell.border = dataCellBorder
                },
                alterCellKey: `${alphabet[sheetColumns.length - 1]}4`,
            },
        ],
        [
            {
                content: 'Dato for mindst 6 måneder fra beregningsdato:',
                col_start: 0,
                merge: `A5:${alphabet[sheetColumns.length - 2]}5`,
            },
            {
                content: formatText(toDate(new Date(period.earliest_end)), reportDateFormat),
                col_start: sheetColumns.length - 1,
                alterCell: (cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'D3D3D3' },
                    }
                    cell.border = dataCellBorder
                },
                alterCellKey: `${alphabet[sheetColumns.length - 1]}5`,
            },
        ],
        [
            {
                content:
                    'Start- og slut-dato for udenlandsopholdet medregnes ikke i sammentællingen jf.:',
                col_start: 0,
                merge: `A6:${alphabet[sheetColumns.length - 3]}6`,
                alterCell: (cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'EEECE1' },
                    }
                },
                alterCellKey: 'A6',
            },
            {
                merge: `${alphabet[sheetColumns.length - 2]}6:${
                    alphabet[sheetColumns.length - 1]
                }6`,
                alterCell: (cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'EEECE1' },
                    }
                    cell.value = {
                        text: 'SKM2003.405.HR',
                        hyperlink: 'https://info.skat.dk/data.aspx?oID=169672',
                        // tooltip: 'skat.dk'
                    }
                },
                alterCellKey: `${alphabet[sheetColumns.length - 2]}6`,
            },
        ],
    ]

    function insertHeaderRow(rowNo, headerRow) {
        var rowToInsert = {}
        for (const headerCol of headerRow) {
            if (headerCol.content) {
                rowToInsert[sheetColumns[headerCol.col_start].key] = headerCol.content
            }
        }
        sheet.insertRow(rowNo, rowToInsert)
        for (const headerCol of headerRow) {
            if (headerCol.merge) {
                sheet.mergeCells(headerCol.merge)
            }
            if (headerCol.alterCellKey && headerCol.alterCell) {
                headerCol.alterCell(sheet.getCell(headerCol.alterCellKey))
            }
        }
    }

    // insert document meta data abve table
    headerRows.forEach((headerRow, idx) => {
        const rowNo = idx + 1
        insertHeaderRow(rowNo, headerRow)
    })

    // insert table header
    sheetColumns.forEach((col, idx) => {
        const colId = `${alphabet[idx]}7`
        sheet.getCell(colId).alignment = {
            wrapText: true,
            horizontal: 'center',
        }
        sheet.getCell(colId).font = metaFont
        sheet.getCell(colId).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'DDD9C3' },
        }
        sheet.getCell(colId).border = dataCellBorder
    })

    const excelBuffer = await workbook.xlsx.writeBuffer()
    const fileData = new Blob([excelBuffer], { type: fileType })
    return fileData
}
